import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, of, Subscription, tap } from 'rxjs';
import { QuestionPreviewComponent } from '../question-preview/question-preview.component';
import { ToastrService } from 'ngx-toastr';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { FullQuizData } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';

@Component({
	selector: 'app-csv-mode',
	standalone: true,
	imports: [CommonModule, FormsModule, QuestionPreviewComponent],
	templateUrl: './csv-mode.component.html',
	styleUrl: './csv-mode.component.scss'
})
export class CSVModeComponent implements OnInit, OnDestroy {

	showPreview: boolean = false;
	isFileUploaded = false;
	fileName: string | null = null;
	fileError: string | null = null;
	selectedFile: File | null = null;
	_quizData: FullQuizData | null = null;
	showValidation = false;
	classroomId!: number;
	quizId!: string;
	subscription = new Subscription();
	uploadCSVResult: any;
	csvQuestions: any[] = [];
	@Output() switchMode = new EventEmitter<'manual' | 'ai' | 'course' | 'csv'|'pdf'>();


	constructor(
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private _quizService: QuizManagementService
	) { }


	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.classroomId = +params.get('classroomId')!;
			this.quizId = params.get('quizId')!;
		});
	}


	onFileSelected(event: any) {
		const file = event.target.files[0];

		if (!file) return;

		if (file.size > 5 * 1024 * 1024) {
			this.fileError = 'File size should be less than 5MB';
			this.isFileUploaded = false;
			return;
		}

		this.subscription.add(
			this._quizService.postQuizCSVUpload(this.quizId, file).pipe(
				tap(response => {
					if (response.Success) {
						this.getquizById();
						this.uploadCSVResult = response.Data;
						this.toastr.success(response.Message);
						this.fileError = null;
						this.selectedFile = file;
						this.fileName = file.name;
						this.isFileUploaded = true;
					} else {
						this.toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this.toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}


	removeFile(event: Event) {
		event.stopPropagation();

		this.fileName = null;
		this.selectedFile = null;
		this.isFileUploaded = false;
	}


	get stats() {
		if (!this.uploadCSVResult) return [];

		return [
			{
				icon: 'bi bi-list-ol',
				label: 'TOTAL ROW',
				value: this.uploadCSVResult.total_rows_processed,
				type: 'blue'
			},
			{
				icon: 'bi bi-cloud-arrow-up',
				label: 'UPLOADED',
				value: this.uploadCSVResult.successfully_uploaded,
				type: 'green'
			},
			{
				icon: 'bi bi-x-circle-fill',
				label: 'FAILED',
				value: this.uploadCSVResult.failed_rows_count,
				type: 'red'
			}
		];
	}


	downloadTemplate(): void {
		this.subscription.add(
			this._quizService.getCSVDownloadTemplate().subscribe({
				next: (res: any) => {
					const blob = new Blob([res.body], {
						type: 'text/csv'
					});
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = 'quiz-template.csv';
					a.click();
					window.URL.revokeObjectURL(url);
				},
				error: () => {
					this.toastr.error('Failed to download template');
				}
			})
		);
	}


	getquizById() {
		if (this.quizId) {
			this.subscription.add(
				this._quizService.getQuizById(this.quizId).subscribe({
					next: (response) => {
						if (response.Success && response.Data) {
							this._quizService.saveQuizData.next(response.Data);
							this._quizData = response.Data;
							this.csvQuestions = response.Data.questions.filter((quest: any) => {
								return quest.topic && quest.subject && quest.topic;
							}).map((q: any) => ({
								question: q.question_text,
								option1: q.options?.[0]?.text || '',
								option2: q.options?.[1]?.text || '',
								option3: q.options?.[2]?.text || '',
								option4: q.options?.[3]?.text || '',
								answer: q.correct_answer,
								difficulty: q.difficulty_level,
								course: response.Data.quiz_name,
								topic: q.topic
							}));
						}
					}
				}));
		}
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
